import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Upload, message, Select } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { STATUS_CODE } from "common/Constants";
import { CALL_API } from "common/API";
import { useDispatch, useSelector } from "react-redux";
import { getAssetsList, getUserInfo } from "redux/selectors";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import { useTranslation } from "react-i18next";
import { setAssetsList } from "redux/actions";

const ModalWrap = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
  }
  .ant-modal-body {
    padding: 0px 24px 24px 24px;
  }
  .custom-upload .ant-upload {
    width: 100%;
  }

  .upload-box {
    width: 100%;
    height: 42px;
    border-radius: 12px;
    background: #cfd6ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #cfd6ff;
  }

  .upload-box:hover {
    background: #bcc6ff;
  }
`;

const { TextArea } = Input;

const ApplyAsset = ({ open, onCancel, data = {}, getData = () => {} }) => {
  const assetsList = useSelector(getAssetsList);
  console.log("assetsList", assetsList);
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const userInfo = useSelector(getUserInfo);
  const [popupOpen, setPopupOpen] = useState(false);

  const dispatch = useDispatch();

  const fetchMasterData = async (fetch_master_data) => {
    const { code, assetsCategories } = await CALL_API(
      `assets/getassets/all`,
      "get"
    );
    if (code === STATUS_CODE.SUCCESS) {
      dispatch(setAssetsList(assetsCategories));
    }
  };

  // Populate form when editing an existing ticket
  useEffect(() => {
    if (data && Object.keys(data).length) {
      form.setFieldsValue({
        assetname: data?.assetname || null,
        title: data?.title || "",
        ticket_description: data?.ticket_description || "",
        priority: data?.priority || null,
        attachment: data?.ticket_filename
          ? [
              {
                uid: "-1",
                name: data.ticket_filename,
                status: "done",
                url: data.ticket_filepath || "",
              },
            ]
          : [],
      });
    }
  }, [data]);

  const handleDocumentSubmit = async (file) => {
    try {
      const initPayload = {
        filename: file.name,
        type: file.type,
      };

      const res = await CALL_API(`assets/ticket-file`, "POST", initPayload);

      if (res?.code !== STATUS_CODE.SUCCESS) {
        throw new Error("Failed to get upload URL");
      }

      const uploadUrl = res?.url;
      const key = res?.key;

      if (!uploadUrl) throw new Error("Upload URL missing");

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (!putRes.ok) throw new Error("Upload failed");

      return key;
    } catch (err) {
      message.error(t("File upload failed"));
      return "";
    }
  };

  const handleFinish = async (values) => {
    try {
      setLoading(true);

      const fileList = values?.attachment || [];
      const fileObj = fileList?.[0]?.originFileObj || null;

      let uploadedKey = data?.ticket_objectkey || "";
      let fileName = data?.ticket_filename || "";

      if (fileList.length === 0) {
        uploadedKey = "";
        fileName = "";
      }

      // If a new file was selected, upload it
      if (fileObj) {
        const videoTypes = [
          "video/mp4",
          "video/mpeg",
          "video/quicktime",
          "video/x-msvideo",
          "video/x-ms-wmv",
          "video/webm",
          "video/ogg",
        ];

        if (
          videoTypes.includes(fileObj.type) ||
          fileObj.type?.startsWith("video/")
        ) {
          message.error(t("Video files are not allowed"));
          return;
        }

        uploadedKey = await handleDocumentSubmit(fileObj);
        fileName = fileObj.name;
        if (!uploadedKey) return;
      }

      const findCategory = assetsList?.find(
        (item) => item?.assetid === values?.assetname
      );
      const selectedAsset = assetsList.find(
        (a) => a.assetid === values.assetname
      );
      const payload = {
        assetname: selectedAsset?.assetname,
        title: values.title,
        ticket_description: values.ticket_description,
        priority: values.priority,
        ticket_filename: fileName,
        ticket_objectkey: uploadedKey,
        userid: userInfo?.userid,
        categoryname: findCategory?.categoryname || "",
        categorytype: findCategory?.categorytype || "",
        model: findCategory?.model || "",
        ticket_status: "Open",
        applied_on: new Date(),
        rejected_reason: "",
        resolved_notes: "",
        assetid: values?.assetname,
      };

      if (data?.ticketid) {
        payload.ticketid = data.ticketid;
      }

      const url = data?.ticketid
        ? `assets/update-ticket/${data.ticketid}`
        : `assets/asset-ticket`;

      const method = data?.ticketid ? "patch" : "post";

      const { code } = await CALL_API(url, method, payload);

      if (code === STATUS_CODE.SUCCESS) {
        await getData();
        setPopupOpen(true);

        setTimeout(() => {
          setPopupOpen(false);
          form.resetFields();
          onCancel();
        }, 2000);
      } else {
        message.error(t("Something went wrong"));
      }
    } catch (err) {
      message.error(t("Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  return (
    <ModalWrap
      title={data?.ticketid ? t("Edit Ticket") : t("Raise a Ticket")}
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={null}
      centered
      width={580}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {/* Asset */}
        <Form.Item
          label={t("Choose Asset")}
          name="assetname"
          rules={[{ required: true, message: t("Asset is required") }]}
        >
          <Select
            placeholder={t("Select Asset")}
            options={assetsList
              ?.filter((item) => item?.assigned_to === userInfo?.userid)
              ?.map((item) => {
                return {
                  id: item?.assetid,
                  value: item?.assetid,
                  label: item?.assetname,
                };
              })}
          />
        </Form.Item>

        {/* Title */}
        <Form.Item
          label={t("Title")}
          name="title"
          rules={[{ required: true, message: t("Title is required") }]}
        >
          <Input placeholder={t("Describe the issue briefly")} />
        </Form.Item>

        {/* Description */}
        <Form.Item
          label={t("Description")}
          name="ticket_description"
          rules={[
            { required: true, message: t("Description is required") },
            { max: 250, message: t("Max 250 chars") },
          ]}
        >
          <TextArea rows={4} maxLength={250} />
        </Form.Item>

        {/* Priority */}
        <Form.Item
          label={t("Priority")}
          name="priority"
          rules={[{ required: true, message: t("Priority is required") }]}
        >
          <Select placeholder={t("Select Priority")}>
            <Select.Option value="Low">Low</Select.Option>
            <Select.Option value="Medium">Medium</Select.Option>
            <Select.Option value="High">High</Select.Option>
            <Select.Option value="Urgent">Urgent</Select.Option>
          </Select>
        </Form.Item>

        {/* Attachment */}
        <Form.Item
          label={t("Attachment (Optional)")}
          name="attachment"
          valuePropName="fileList"
          getValueFromEvent={(e) => e?.fileList}
        >
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            className="custom-upload"
          >
            {!form.getFieldValue("attachment")?.length && (
              <div className="upload-box">
                <UploadOutlined />
                <span style={{ marginLeft: 8 }}>{t("Upload File")}</span>
              </div>
            )}
          </Upload>
        </Form.Item>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <ButtonOutlined
            onClick={() => {
              form.resetFields();
              onCancel();
            }}
          >
            {t("Cancel")}
          </ButtonOutlined>

          <ButtonFilled type="primary" htmlType="submit" loading={loading}>
            {data?.ticketid ? t("Save Changes") : t("Submit")}
          </ButtonFilled>
        </div>
      </Form>

      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type="success"
        message={data?.ticketid ? t("Saved") : t("Submitted")}
      />
    </ModalWrap>
  );
};

export default ApplyAsset;
